/**
 * The pieces every report tab is drawn out of.
 *
 * Its own file rather than five copies: the five tabs are the same three shapes
 * with different numbers in them - a strip of figures, a two-line month chart,
 * and a ranked list with a bar behind each row - and a chart that looked
 * different on the Expenses tab than on the Sales tab would read as a different
 * kind of chart.
 *
 * Nothing here reads the catalogue. Every caller passes finished text, the rule
 * `_shared/formBits.tsx` already follows, so a control cannot decide the wording
 * for a screen it knows nothing about.
 */
import type { CSSProperties, ReactNode } from "react";
import { Card, CardHead, fmtNum } from "../../ui";
import { AreaChart } from "../../ui/Sparkline";

/** `2026-08` as the operator reads it. Month and a two-digit year, because a
 *  twelve-month strip of full dates is a wall of numbers. */
export function monthLabel(key: string, locale: string): string {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString(locale.startsWith("et") ? "et-EE" : "en-GB", { month: "short", year: "2-digit" });
}

/**
 * One figure in the strip above a report.
 *
 * The inner `<span>` wrapper is load-bearing rather than decoration: `.crm-q` is
 * a ROW (`display: flex`, for the icon-tile-beside-the-figure shape the dashboard
 * uses), so three bare children would stand side by side instead of stacking.
 * Same markup as the expenses strip, which is the tile without an icon.
 */
export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "ok" | "bad" }) {
  return (
    <div className="crm-q">
      <span className="min-w-0">
        <span className="crm-q-lab block">{label}</span>
        <span
          className="crm-q-val block"
          style={tone ? { color: tone === "ok" ? "var(--color-success)" : "var(--color-destructive)" } : undefined}
        >
          {value}
        </span>
        {sub ? <span className="crm-prow-s block">{sub}</span> : null}
      </span>
    </div>
  );
}

/**
 * The strip itself: as many columns as it was given, never fewer than one.
 *
 * The count goes out as `--crm-quick-cols`, NOT as an inline
 * `grid-template-columns`. That is the whole difference between a strip that
 * folds on a phone and one that does not: an inline template cannot be
 * overridden by any stylesheet rule short of `!important`, so `index.css`'s
 * narrow-screen folds would lose silently and five figures would print on top of
 * each other at 375px. See the comment on `.crm-quick`.
 *
 * `--crm-quick-cols-xs` is 2 rather than the default 1, and only because these
 * figures earn it: every one of them is a short money or hour value under a
 * one-or-two-word label, which is what that variable's own note asks for before
 * raising it.
 */
export function StatStrip({ children, columns }: { children: ReactNode; columns: number }) {
  return (
    <Card
      className="crm-quick"
      style={{ "--crm-quick-cols": Math.max(1, columns), "--crm-quick-cols-xs": 2 } as CSSProperties}
    >
      {children}
    </Card>
  );
}

export interface SeriesLine {
  label: string;
  values: number[];
  color: string;
}

/**
 * Two series over the same months, on one scale.
 *
 * One scale is the whole point - `AreaChart` takes `compare` for exactly this -
 * because two lines drawn against their own maxima would show a small number and
 * a large one at the same height. The month labels sit under the chart rather
 * than on it: the shared chart draws no axis, and inventing one here would be a
 * second chart component in the same app.
 */
export function MonthChart({
  title,
  sub,
  months,
  lines,
  locale,
  actions,
}: {
  title: string;
  sub?: string;
  months: string[];
  lines: [SeriesLine, SeriesLine?];
  locale: string;
  actions?: ReactNode;
}) {
  const [main, compare] = lines;
  return (
    <Card>
      <CardHead title={title} sub={sub} actions={actions} />
      <div className="crm-chart">
        <AreaChart values={main.values} compare={compare?.values} color={main.color} />
      </div>
      <div className="crm-rep-axis">
        {months.map((m) => (
          <span key={m}>{monthLabel(m, locale)}</span>
        ))}
      </div>
      <div className="crm-legend">
        <span>
          <i style={{ background: main.color }} />
          {main.label}
        </span>
        {compare ? (
          <span>
            <i style={{ background: compare.color }} />
            {compare.label}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

export interface BreakdownRow {
  key: string;
  label: string;
  /** What the bar is drawn from and the rows are ranked by. May be negative -
   *  the bar is drawn from its size and the row says its sign. */
  weight: number;
  /** What is printed on the right. */
  value: string;
  sub?: string;
  /** Overrides the list's colour for this row. The one caller that needs it is
   *  the profit-by-month list, where a bar drawn in the same colour would make a
   *  loss look exactly like a gain of the same size. */
  color?: string;
}

/**
 * A ranked list with a bar behind each row.
 *
 * The bar is a share of the BIGGEST row, not of the total: this is a "what is
 * the largest heading" list, and normalising to the sum makes every breakdown of
 * more than a handful of rows look uniformly flat.
 */
export function Breakdown({
  title,
  sub,
  rows,
  empty,
  color = "var(--color-primary)",
}: {
  title: string;
  sub?: string;
  rows: BreakdownRow[];
  empty: string;
  color?: string;
}) {
  const top = Math.max(1, ...rows.map((r) => Math.abs(r.weight)));
  return (
    <Card>
      <CardHead title={title} sub={sub} />
      {rows.length ? (
        <div className="crm-rep-list">
          {rows.map((r) => (
            <div key={r.key} className="crm-rep-row">
              <span className="crm-rep-top">
                <b className="crm-trunc">{r.label}</b>
                <span className="crm-rep-v">{r.value}</span>
                {r.sub ? <span className="crm-rep-s">{r.sub}</span> : null}
              </span>
              <span className="crm-fbar">
                <i style={{ width: `${Math.round((Math.abs(r.weight) / top) * 100)}%`, background: r.color ?? color }} />
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="crm-rep-empty">{empty}</div>
      )}
    </Card>
  );
}

/** The count under a card head, where a card has a countable subject. */
export function countSub(n: number, word: string): string {
  return `${fmtNum(n)} ${word}`;
}
